import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import Validator, { ValidationError } from "fastest-validator";
import type { TFunction } from "i18next";
import { useOcProperty } from "oc/hooks";

interface UseOcPasswordInputResult {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  error: boolean;
  setError: Dispatch<SetStateAction<boolean>>;
  helperText: string;
  setHelperText: Dispatch<SetStateAction<string>>;
  isValid: (options: { checkPasswordPolicy: boolean }) => boolean;
  validate: (options: { checkPasswordPolicy: boolean }) => void;
}

const validator = new Validator();

export const useOcPasswordInput = (): UseOcPasswordInputResult => {
  const { t }: { t: TFunction } = useTranslation();
  const { data: passwordPolicy } = useOcProperty({
    resource: "system",
    key: "passwordPolicy",
  });

  const passwordSchema = {
    password: {
      type: "string",
      min: 1,
      messages: {
        string: "WRONG_PASSWORD",
        stringMin: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
        required: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
      },
    },
  };

  const checkPassword = validator.compile(passwordSchema);

  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [helperText, setHelperText] = useState<string>("");

  const isValidPassword = (val: string, policy?: string): boolean => {
    const isValidLength = val.length >= 8;
    const containsLowercase = /[a-z]/.test(val);
    const containsUppercase = /[A-Z]/.test(val);
    const containsLetter = /[a-zA-Z]/.test(val);
    const containsDecimal = /\d/.test(val);
    const containsSpecial = /[-+_!@#$%^&*.,?]/.test(val);

    if (policy === "strong") {
      return (
        isValidLength &&
        containsLowercase &&
        containsUppercase &&
        containsDecimal &&
        containsSpecial
      );
    }
    return containsLetter && containsDecimal && isValidLength;
  };

  const isValid = ({
    checkPasswordPolicy,
  }: {
    checkPasswordPolicy: boolean;
  }): boolean => {
    const basicValid = checkPassword({ password: value }) === true;
    if (checkPasswordPolicy) {
      return isValidPassword(value, passwordPolicy);
    }
    return basicValid;
  };

  const validate = ({
    checkPasswordPolicy,
  }: {
    checkPasswordPolicy: boolean;
  }): void => {
    setError(false);
    setHelperText("");

    const basicResult = checkPassword({ password: value });
    if (checkPasswordPolicy && !isValidPassword(value, passwordPolicy)) {
      setError(true);
      setHelperText(t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_WRONG"));
      return;
    }

    if (basicResult !== true) {
      (basicResult as ValidationError[]).forEach((err) => {
        if (err.field === "password") {
          setError(true);
          setHelperText(err.message as string);
        }
      });
    }
  };

  return {
    value,
    setValue,
    error,
    setError,
    helperText,
    setHelperText,
    isValid,
    validate,
  };
};

export default useOcPasswordInput;
